import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import Logo3D from "./Logo3D";
import logoUrl from "../../images/Recurso 7.svg?url";
import imagenH1 from "../../images/trato.jpg"; // 👈 fondo mobile

const WORDS = ["más!", "mejor!", "online!"];

export default function HeroSection({ onWhatsAppClick, isDownMd }) {
  const [wordIndex, setWordIndex] = React.useState(0);
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  React.useEffect(() => {
    const id = setInterval(() => setWordIndex(i => (i + 1) % WORDS.length), 2300);
    return () => clearInterval(id);
  }, []);

  const currentWord = WORDS[wordIndex];

  return (
<Box
  sx={{
    position: "relative",
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
    alignItems: { xs: "start", md: "center" },
    minHeight: { xs: 300, md: "68vh" },   // 👈 más compacto en mobile
    maxHeight: { xs: 380, md: "none" },   // 👈 límite en mobile
    overflow: "hidden",                   // oculta exceso de imagen
    pt: { xs: 12, md: 2, xl: 0},
    pb: { xs: 0, md: 0 },
    px: { xs: 2, md: 2, xl: 8},
  }}
>
      {/* Fondo con foto SOLO en mobile */}
<Box
  aria-hidden
  sx={{
    display: { xs: "block", md: "none" },
    position: "absolute",
    inset: 0,
    maxHeight: 420,             // 👈 altura máxima del fondo
    backgroundImage: `url(${imagenH1})`,
    backgroundSize: "cover",
    backgroundPosition: "center top",
    backgroundRepeat: "no-repeat",
    zIndex: 0,
  }}
/>
{/* Overlay SOLO en mobile */}
<Box
  aria-hidden
  sx={{
    display: { xs: "block", md: "none" },
    position: "absolute",
    inset: 0,
    maxHeight: 420,             // 👈 mismo límite que el fondo
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.75) 10%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.35) 90%)",
    zIndex: 1,
    pointerEvents: "none",
  }}
/>

{/* Columna izquierda (texto) */}
<Box
  sx={{
    position: "relative",
    zIndex: 2, // sobre el fondo mobile
    px: { xs: 1, md: 6 },
  }}
>
  <Typography
    variant="h2"
    sx={{
      color: "#000",
      fontWeight: 700,
      fontSize: { xs: "2rem", md: "3rem", lg: "3.5rem" },
      lineHeight: 1.1,
      mb: 0.8,
      color: { xs: "#fff", md: "inherit" },
      textShadow: "0 4px 8px rgba(0,0,0,0.3)", // 👈 sombra suave
    }}
  >
    ¡Impulsamos tu negocio
  </Typography>

  <Typography
    component="div"
    variant="h2"
    sx={{
      display: "flex",
      alignItems: "baseline",
      gap: 1,
      fontWeight: 800,
      fontSize: { xs: "1.8rem", md: "3rem", lg: "3.5rem" },
      lineHeight: 1.1,
      mb: 5,
      color: { xs: "#fff", md: "inherit" },
      textShadow: "0 4px 8px rgba(0,0,0,0.3)", // 👈 también acá
    }}
    aria-live="polite"
  >
    <span>para vender&nbsp;</span>
    <Box
      component="span"
      sx={{ position: "relative", minWidth: { xs: 90, md: 120 } }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={currentWord}
          initial={
            reduced ? { opacity: 0 } : { y: 28, opacity: 0, rotateX: -35 }
          }
          animate={
            reduced ? { opacity: 1 } : { y: 0, opacity: 1, rotateX: 0 }
          }
          exit={
            reduced ? { opacity: 0 } : { y: -28, opacity: 0, rotateX: 35 }
          }
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: "inline-block" }}
        >
          <span
            style={{
              color: "#F35A3A",
              fontWeight: 900,
              textShadow: "0 4px 8px rgba(0,0,0,0.2)", // 👈 también al highlight
            }}
          >
            {currentWord}
          </span>
        </motion.span>
      </AnimatePresence>
    </Box>
  </Typography>

  {/* Botones */}
  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
    <Button
      onClick={() => {
        const target = document.getElementById("sabermas");
        if (target)
          target.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      variant="contained"
      size={isDownMd ? "medium" : "large"}
    sx={{
      borderRadius: "20px",
      px: 3,
      py: 1.25,
      minHeight: 44,
      boxShadow: "0 4px 12px rgba(243,90,58,0.35)", // glow suave
      transition: "transform .25s ease, box-shadow .25s ease", // 👈 animaciones suaves
      "&:hover": {
        transform: "scale(1.05)", // 👈 se agranda un poquito
        boxShadow: "0 6px 16px rgba(243,90,58,0.45)",
      },
    }}
  >
      <Typography fontWeight={700} color="#fff">
        Saber más
      </Typography>
    </Button>

    <Button
      onClick={onWhatsAppClick}
      variant="outlined"
      size={isDownMd ? "medium" : "large"}
    sx={{
      borderRadius: "20px",
      px: 3,
      py: 1.25,
      minHeight: 44,
      color: { xs: "#fff", md: "inherit" },
      borderColor: { xs: "#fff", md: "currentColor" },
      boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
      transition: "transform .25s ease, box-shadow .25s ease",
      "&:hover": {
        transform: "scale(1.05)", // 👈 también se agranda
        backgroundColor: "rgba(255,255,255,0.08)",
        boxShadow: "0 5px 14px rgba(0,0,0,0.25)",
      },
    }}
  >
      <Typography fontWeight={700}>COMENCEMOS AHORA</Typography>
    </Button>
  </Box>
</Box>

      {/* Columna derecha: logo 3D (oculto en mobile) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "30vw",
            height: "30vw",
            maxWidth: 480,
            maxHeight: 480,
            minWidth: 260,
            minHeight: 260,
            position: "relative",
          }}
        >
          <Logo3D svgUrl={logoUrl} depth={12} />
        </Box>
      </Box>
    </Box>
  );
}